import React, { useState } from 'react';
import { useFormik } from 'formik';
import { TextField as TextInput } from '@material-ui/core';
import {
  KeyboardDatePicker as DatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { t } from 'src/i18n';
import PrimaryButton from 'src/components/PrimaryButton/PrimaryButton.component';
import * as Yup from 'yup';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useStyles,
  FormLabel,
  CenterRow,
  ButtonWrapper
} from '../VerifyIdentity.style';
import { isKeyPressEnterEvent, parseJSON } from 'src/utils/common.util';
import Routes from 'src/modules/Navigator/Navigator.route';
import API from 'src/api/api';
import ErrorMessageWithIcon from 'src/components/ErrorMessageWithIcon/ErrorMessageWithIcon.component';
import ErrorPopup from 'src/components/ErrorPopup/ErrorPopup';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';
import { pathOr } from 'ramda';

interface IProps {
  setStepVerify(step: number): void;
}

const VerifyIdentityForm: React.FC<IProps> = ({ setStepVerify }) => {
  const classes = useStyles();
  const [selectedDate, handleDateChange] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const query = new URLSearchParams(useLocation().search);
  const history = useHistory();
  const [lastNameError, setLastNameError] = React.useState<string>('');
  const [dateOfBirthError, setDateOfBirthError] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showErrorPopup, setShowErrorPopup] = React.useState<boolean>(false);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
    setFieldError,
    setFieldValue
  } = useFormik({
    initialValues: {
      lastName: '',
      dob: ''
    },

    onSubmit: async (val) => {
      setIsLoading(true);
      const keyToSet = query.get('key');
      const accountId = (parseJSON('tenantInfo') as any).accountId;

      try {
        const response = await API().post('/auth/v1/public/verify-identity', {
          accountId,
          key: decodeURIComponent(keyToSet || ''),
          nameLast: val.lastName.trim(),
          birthDate: new Date(val.dob).toUTCString()
        });
        if (response.status === 204) {
          history.push(
            `${Routes.SetNewPassword}?key=${encodeURIComponent(keyToSet || '')}`
          );
          setIsLoading(false);
        }
      } catch (err) {
        if (err.response.status) {
          const errorMessageFromAPI = pathOr(err.message, [
            'response',
            'data',
            'message'
          ])(err);
          setErrorMessage(errorMessageFromAPI);
          setShowErrorPopup(true);
          setIsLoading(false);
        }
      }
    },

    validationSchema: Yup.object().shape({
      lastName: Yup.string()
        .trim()
        .required(t('register.error.required.lastName')),

      dob: Yup.date()
        .required(t('register.error.required.dob'))
        .typeError(t('register.error.format.dob'))
        .max(new Date(), t('register.error.maxDate.dob'))
    })
  });

  React.useEffect(() => {
    setIsLoading(true);
    const keyToVerify = decodeURIComponent(query.get('key') || '');

    API()
      .post('/auth/v1/public/verify-reset-key', {
        key: keyToVerify
      })
      .then((response) => {
        if (response.status === 204 || response.status === 200) {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!err.response.status) {
          setIsLoading(false);

          return;
        }
        setStepVerify(0);
      });
  }, []);

  const handleOnChangeDate = (date) => {
    handleDateChange(date);
    setFieldError('dob', '');
  };

  const handleKeydown = (event) => {
    if (isKeyPressEnterEvent(event)) {
      handleSubmit();
    }

    return false;
  };

  const trimWhenBlur = (fieldname) => (e) => {
    const trimValue = e.target.value.trim();
    setFieldValue(fieldname, trimValue, true);
  };

  React.useEffect(() => {
    values.dob = !selectedDate ? '' : moment(selectedDate).format('YYYY-MM-DD');
  }, [selectedDate]);

  React.useEffect(() => {
    if (!!errors.lastName && !!touched.lastName) {
      setLastNameError(errors.lastName);
    } else {
      setLastNameError('');
    }

    if (!!errors.dob && !!touched.dob) {
      setDateOfBirthError(errors.dob);
    } else {
      setDateOfBirthError('');
    }
  }, [errors, touched]);

  return (
    <LoadingContainer isLoading={isLoading}>
      <form onSubmit={handleSubmit} autoComplete="off" style={{ padding: 25 }}>
        <div className={classes.mb20}>
          <FormLabel>{t('register.label.lastName')} *</FormLabel>
          <TextInput
            onChange={handleChange}
            name="lastName"
            value={values.lastName}
            type="text"
            label={null}
            autoComplete="off"
            variant="outlined"
            placeholder={'Last Name'}
            inputProps={{
              maxLength: 50,
              style: { padding: 10 },
              onBlur: trimWhenBlur('lastName')
            }}
            style={{ width: '100%' }}
            onBlur={handleBlur}
            error={!!errors.lastName && !!touched.lastName}
            onKeyDown={handleKeydown}
          />
          {lastNameError && (
            <ErrorMessageWithIcon message={lastNameError} size="small" />
          )}
        </div>

        <div className={classes.mb20}>
          <FormLabel className={classes.mt30}>
            {t('register.label.dob')} *
          </FormLabel>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <DatePicker
              id="dob"
              name="dob"
              placeholder="MM/DD/YYYY"
              value={selectedDate ? selectedDate : null}
              onChange={handleOnChangeDate}
              format={'MM/DD/YYYY'}
              inputVariant="outlined"
              style={{ width: '100%' }}
              inputProps={{ style: { padding: 10 } }}
              disableFuture={true}
              data-testid="datePicker"
              onBlur={handleBlur}
              error={!!errors.dob && !!touched.dob}
              helperText={null}
            />
          </MuiPickersUtilsProvider>
          {dateOfBirthError && (
            <ErrorMessageWithIcon message={dateOfBirthError} size="small" />
          )}
        </div>

        <ErrorPopup
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          errorMessage={errorMessage}
        />

        <CenterRow>
          <ButtonWrapper>
            <PrimaryButton
              type="submit"
              variant="contained"
              isShowIcon={true}
              title={t('general.next')}
            />
          </ButtonWrapper>
        </CenterRow>
      </form>
    </LoadingContainer>
  );
};

export default VerifyIdentityForm;
